import { model as i18n } from '!/i18n'
import { epgCategoriesModel, type TvCategoryEx } from '$/epg/categories'
import { lookbehind } from '@/effector'
import { A } from '@mobily/ts-belt'
import { combine, createEvent, createStore, restore, sample } from 'effector'
import { delay } from 'patronum'
import { profilesModel } from '~/entities/profiles'
import { loadEpgCategoriesModel } from '~/features/epg/categories/load'
import { loadMoreEpgCategoriesModel } from '~/features/epg/categories/load-more'
import {
  getCategoryIdFromQuery,
  selectEpgCategoryModel,
} from '~/features/epg/categories/select'
import { updateEpgChannelModel } from '~/features/epg/channels/update'
import { id, noop } from '~/shared/helpers'
import { translateCategory } from './l10n'

// load initial categories list
export const init = createEvent<{ query: URLSearchParams }>()

// reset widget on epg page left
export const left = createEvent()

// reset widget on sign out
export const reset = createEvent()

const resetActiveCategoryId = createEvent<void>()

const updateActiveCategoryId = createEvent<void>()

// load more categories
export const more = loadMoreEpgCategoriesModel.load

// select category
export const select = selectEpgCategoryModel.setActiveId

// categories to show in widget
export const $categories = createStore<TvCategoryEx[]>([])

// flag to indicate that there are no more categories to load
export const $isLast = epgCategoriesModel.$isLast

// currently selected category id
export const $selected = selectEpgCategoryModel.$activeId

// pipe to check if category is selected
export const $isCategorySelectedPipe = combine(
  $selected,
  (selected) => (category: TvCategoryEx) => category.id === selected
)

// load initial categories list
sample({
  clock: init,
  fn: ({ query }) => ({ query, category: getCategoryIdFromQuery(query) }),
  target: loadEpgCategoriesModel.load,
})

// auto select first or url-chosen category
sample({
  clock: [init, loadEpgCategoriesModel.$ready],
  source: {
    init: restore(init, null).reset(left, reset),
    ready: loadEpgCategoriesModel.$ready,
  },
  filter: ({ init, ready }) => init != null && ready,

  fn: ({ init }) => init!,
  target: selectEpgCategoryModel.autoSetActiveId,
})

lookbehind({
  source: updateEpgChannelModel.reloadFavoritesFx.done,
  after: profilesModel.$activeProfile,
  within: 500,
  target: resetActiveCategoryId.prepend(noop),
})

delay({
  source: resetActiveCategoryId,
  timeout: 0,
  target: updateActiveCategoryId,
})

sample({
  clock: updateActiveCategoryId,
  target: [
    selectEpgCategoryModel.$activeId.reinit,
    selectEpgCategoryModel.autoSetActiveId,
  ],
})

// wait for initial categories list to be loaded, then concat made-up and loaded categories
// also update categories list on load more categories event
sample({
  clock: [
    loadEpgCategoriesModel.$ready,
    epgCategoriesModel.$loaded,

    // TODO: remove this line if featured request will be faster
    //       now this allows to show featured channels dynamically when they are loaded
    epgCategoriesModel.$madeup,
    i18n.$locale,
  ],
  source: {
    i18n: i18n.$t,
    madeup: epgCategoriesModel.$madeup,
    loaded: epgCategoriesModel.$loaded,
  },
  filter: loadEpgCategoriesModel.$ready,
  fn: ({ i18n: { t }, madeup, loaded }) =>
    A.concat(A.map(madeup, translateCategory(t)), loaded),
  target: $categories,
})

// show favorite category in case favorite channels are updated
sample({
  clock: [updateEpgChannelModel.$favorites, updateEpgChannelModel.liked],
  source: {
    favorites: updateEpgChannelModel.$favorites,
    likedId: restore(updateEpgChannelModel.liked, null).reset(reset),
  },
  filter: ({ favorites, likedId }) => favorites.length > 0 || likedId != null,
  fn: ({ favorites, likedId }) => {
    const ids = favorites.map(id)
    return likedId == null ? ids : ids.concat(likedId)
  },
  target: epgCategoriesModel.addFavoriteCategory,
})

// hide favorite category in case favorite channels are updated
sample({
  clock: updateEpgChannelModel.reloadFavoritesFx.done,
  source: updateEpgChannelModel.$favorites,
  filter: (favorites) => favorites.length === 0,
  target: epgCategoriesModel.removeFavoriteCategory,
})

//
// left and reset logic
//

sample({
  clock: left,
  target: [
    selectEpgCategoryModel.$activeId.reinit,
    loadEpgCategoriesModel.$favoritesReady.reinit,
    epgCategoriesModel.removeFavoriteCategory,
  ],
})

sample({
  clock: reset,
  target: [
    epgCategoriesModel.$loaded.reinit,
    epgCategoriesModel.$madeup.reinit,
    epgCategoriesModel.$isLast.reinit,
    loadEpgCategoriesModel.$othersReady.reinit,
    loadEpgCategoriesModel.$favoritesReady.reinit,
  ],
})
